import {WriteButton, WriteFileInput, WriteIconButton} from '../../common/buttons/WriteActions';
import {rememberOriginalName} from '../../../services/SignedImageUpload';
import {getCurrencySymbol} from '../../../common/currency';
import {useEffect, useRef, useState} from 'react';
import {Autocomplete, Box, Card, CardMedia, FormControl, Grid, IconButton, InputAdornment, InputLabel, MenuItem, Select, Stack, TextField} from '@mui/material';
import {DTO_STATE, RECIPE_SIZE} from '@mybake/ui/common/dictionary';
import {IRecipe} from '../../../models/Recipe/IRecipe';
import {ISubOrder} from '../../../models/Order/ISubOrder';
import CloseIcon from '@mui/icons-material/Close';
import RestoreIcon from '@mui/icons-material/Restore';
import {fetchImage, fetchImageV2} from '../../../services/ImageService';
import {IOrderImage} from '../../../models/Order/IOrderImage';
import {IPackage} from "../../../models/Packages/IPackage";
import {PackageSelector} from "../PackageSelector";
import AddIcon from "@mui/icons-material/Add";
import {useAppTranslation} from "@mybake/ui/hooks/useAppTranslation";
import {IDecoration} from '../../../models/Decorations/IDecoration';
import {DecorationSelector} from '../DecorationSelector';
import {calculateSubOrderCost, roundMoney} from '../subOrderCost';
import {useCurrency} from '../../../hooks/useCurrency';
import {userAPI} from '../../../services/UserServices';
import {IProductType} from '../../../models/ProductTypes/IProductType';
import {calculateRecommendedPrice} from '../recommendedPrice';
import {LocalizedDecimalField} from '../../common/inputs/LocalizedDecimalField';

interface Props {
    subOrder: ISubOrder;
    recipes: IRecipe[];
    packages: IPackage[] | undefined;
    decorations: IDecoration[] | undefined;
    productTypes: IProductType[] | undefined;
    deleteSubOrder: () => void;
    saveSubOrderChanges: (subOrder: ISubOrder) => void;
    handleNewFiles: (files: Map<string, File>) => void;
}

interface Image extends IOrderImage {
    recipeName: string;
    url: string;
}

const EditSubOrder = ({subOrder, recipes, packages, decorations, productTypes, deleteSubOrder, saveSubOrderChanges, handleNewFiles}: Props) => {
    const {t} = useAppTranslation();
    const {currency, formatCurrency} = useCurrency();
    const {data: user} = userAPI.useGetUserDataQuery();
    const [editedSubOrder, setEditedSubOrder] = useState<ISubOrder>({...subOrder});
    const [newPrice, setNewPrice] = useState<number>(editedSubOrder.price);
    const [orderCount, setOrderCount] = useState<number>(editedSubOrder.count);
    const [isValueSelected, setIsValueSelected] = useState(editedSubOrder.recipeName && editedSubOrder.recipeName !== '');
    const [fillingName, setFillingName] = useState(editedSubOrder.fillingName);
    const [selectedRecipe, setSelectedRecipe] = useState<IRecipe>();
    const [recipeCost, setRecipeCost] = useState(editedSubOrder.productCost);
    const [newProfit, setNewProfit] = useState<number>(recipeCost - newPrice);
    const [initRender, setInitRender] = useState(true);
    const [loadedImages, setLoadedImages] = useState(false);
    const [previewUrls, setPreviewUrls] = useState<string[] | null>(null);
    const [fileMap, setFileMap] = useState<Map<string, File>>(new Map());
    const [urlMap, setUrlMap] = useState<Map<string, string>>(new Map());
    const [fileHashes, setFileHashes] = useState<Set<string>>(new Set());

    const size =
        editedSubOrder.recipeCounter.toString().length === 1 && editedSubOrder.recipeCounter !== 0
            ? `${editedSubOrder.recipeCounter}.00`
            : editedSubOrder.recipeCounter.toString().length === 3
                ? `${editedSubOrder.recipeCounter}0`
                : editedSubOrder.recipeCounter === 0
                    ? ''
                    : editedSubOrder.recipeCounter.toString() || '1.00';
    const inputCountRef = useRef<HTMLInputElement>(null);
    const inputPriceRef = useRef<HTMLInputElement>(null);
    const fillingError = subOrder.fillingId === 0;

    let recipesNameList: string[] = [];
    recipes?.map((r) => recipesNameList.push(r.recipeName || ''));

    const handleRecipeName = (e: any, name: string | null) => {
        setIsValueSelected(!!name);
        if (name) {
            recipes?.forEach((r) => {
                const recipe = r.recipeName === name;

                if (recipe) {
                    setEditedSubOrder((prevState) => {
                        const hasFillings = (r.fillings?.length ?? 0) > 0;

                        return {
                            ...prevState,
                            recipeName: name,
                            recipeId: r.id,
                            ...(!hasFillings && {
                                fillingId: 0,
                                fillingName: '',
                            }),
                        };
                    });

                    if (!r.fillings?.length) {
                        setFillingName('');
                    }

                    setSelectedRecipe(r);
                    /*if (r.isLinear) {
                        setRecipeSize(getRecipeSize(r));
                    } else {
                        const sizes = r.proportionInfos.map((pi) => pi.proportion);
                        setRecipeSize(sizes);
                    }*/

                    return;
                }
            });
        }
    };

    const handleFilling = (e: any, name: any) => {
        setFillingName(name.props.value);
        const fillingId = selectedRecipe?.fillings && selectedRecipe?.fillings.length > 0 ? selectedRecipe.fillings.find((el) => el.fillingName === name.props.value)?.id || 0 : 0;
        setEditedSubOrder((prev) => {
            return {
                ...prev,
                fillingId: fillingId,
            };
        });
    };

    const handleAddPackageField = () => {
        setEditedSubOrder((prev) => ({
            ...prev,
            packages: [
                ...(prev.packages || []),
                {
                    id: crypto.randomUUID(),
                    packageId: 0,
                    name: '', // Пустое значение по умолчанию
                    quantity: 1,
                    state: DTO_STATE.DTO_NEW
                }
            ]
        }));
    };

    const handleUpdatePackageField = (index: number, updatedValue: {
        packageId: number | string;
        name: string;
        quantity: number
    }) => {
        setEditedSubOrder((prev) => {
            const newPackages = [...(prev.packages || [])];
            const currentPackage = newPackages[index];

            const newState = currentPackage?.state === DTO_STATE.DTO_NEW
                ? DTO_STATE.DTO_NEW
                : DTO_STATE.DTO_MODIFIED;

            newPackages[index] = {
                ...currentPackage,
                packageId: updatedValue.packageId,
                name: updatedValue.name,
                quantity: updatedValue.quantity,
                state: newState
            };
            return {...prev, packages: newPackages};
        });
    };

    const handleRemovePackageField = (index: number) => {
        setEditedSubOrder((prev) => {
            if (!prev.packages) return prev;

            const newPackages = [...prev.packages];
            const targetPackage = newPackages[index];

            if (targetPackage.state === DTO_STATE.DTO_NEW) {
                return {
                    ...prev,
                    packages: newPackages.filter((_, i) => i !== index)
                };
            } else {
                return {
                    ...prev,
                    packages: newPackages.map((pkg, i) =>
                        i === index ? {...pkg, state: DTO_STATE.DTO_DELETED} : pkg
                    )
                };
            }
        });
    };

    const handleRestorePackageField = (index: number) => {
        setEditedSubOrder((prev) => {
            if (!prev.packages) return prev;

            return {
                ...prev,
                packages: prev.packages.map((pkg, i) =>
                    i === index ? {...pkg, state: DTO_STATE.DTO_UNCHANGED} : pkg
                )
            };
        });
    };

    const handleAddDecorationField = () => {
        setEditedSubOrder((prev) => ({
            ...prev,
            decorations: [
                ...(prev.decorations || []),
                {
                    id: crypto.randomUUID(),
                    decorationId: '',
                    name: '',
                    quantity: 1,
                    state: DTO_STATE.DTO_NEW,
                },
            ],
        }));
    };

    const handleUpdateDecorationName = (id: string, decorationName: string) => {
        const decorationId = decorations?.find(x => x.name === decorationName)?.id || '';
        setEditedSubOrder((prev) => ({
            ...prev,
            decorations: (prev.decorations || []).map((decoration) => decoration.id === id
                ? {
                    ...decoration,
                    decorationId,
                    name: decorationName,
                    state: decoration.state === DTO_STATE.DTO_NEW
                        ? DTO_STATE.DTO_NEW
                        : DTO_STATE.DTO_MODIFIED,
                }
                : decoration),
        }));
    };

    const handleUpdateDecorationCount = (id: string, quantity: number) => {
        setEditedSubOrder((prev) => ({
            ...prev,
            decorations: (prev.decorations || []).map((decoration) => decoration.id === id
                ? {
                    ...decoration,
                    quantity: Math.max(0, quantity),
                    state: decoration.state === DTO_STATE.DTO_NEW
                        ? DTO_STATE.DTO_NEW
                        : DTO_STATE.DTO_MODIFIED,
                }
                : decoration),
        }));
    };

    const handleRemoveDecoration = (id: string) => {
        setEditedSubOrder((prev) => {
            const decoration = prev.decorations?.find(x => x.id === id);
            if (!decoration) return prev;

            return {
                ...prev,
                decorations: decoration.state === DTO_STATE.DTO_NEW
                    ? prev.decorations?.filter(x => x.id !== id)
                    : prev.decorations?.map(x => x.id === id
                        ? {...x, state: DTO_STATE.DTO_DELETED}
                        : x),
            };
        });
    };

    const handleRestoreDecoration = (id: string) => {
        setEditedSubOrder((prev) => ({
            ...prev,
            decorations: prev.decorations?.map(x => x.id === id
                ? {...x, state: DTO_STATE.DTO_UNCHANGED}
                : x),
        }));
    };

    const [lastFetchedRecipeId, setLastFetchedRecipeId] = useState<string | null>(null);
    const [selectedPackagesNames, setSelectedPackagesNames] = useState<string[]>([]);
    const selectedPackagesCount = editedSubOrder.packages?.filter(x => x.state !== DTO_STATE.DTO_DELETED).length || 0;
    const availableOptionsCount = packages?.length || 0;
    const isAddDisabled = selectedPackagesCount >= availableOptionsCount;
    const selectedDecorationIds = editedSubOrder.decorations
        ?.filter(x => x.state !== DTO_STATE.DTO_DELETED && x.decorationId)
        .map(x => x.decorationId) || [];
    const isAddDecorationDisabled = !decorations?.length || selectedDecorationIds.length >= decorations.length;
    const recommendation = calculateRecommendedPrice({recipe: selectedRecipe, selectedProportion: editedSubOrder.recipeCounter, count: editedSubOrder.count, selectedDecorations: editedSubOrder.decorations, productTypes, decorations});

    const handleRecipeCounter = (e: any, counter: any) => {
        setEditedSubOrder((prev) => {
            return {
                ...prev,
                recipeCounter: Number(counter.props.value),
            };
        });
    };

    const handlePrice = (value: number | undefined) => {
        const price = value ?? 0;
        setNewPrice(price);
        setEditedSubOrder((prev) => {
            return {
                ...prev,
                price,
            };
        });
    };

    const handleOrderCount = (e: any) => {
        setOrderCount(Number(e.target.value));
        setEditedSubOrder((prev) => {
            return {
                ...prev,
                count: Number(e.target.value),
            };
        });
    };
    const buttonStyle = {lineHeight: '1.25rem' /* fontSize: '0.75rem' */};
    const autoCompleteLabelStyle = {'& .MuiInputLabel-root': {pointerEvents: 'none'}};

    const calculateProductCost = () => {
        const tempCost = calculateSubOrderCost({
            recipe: selectedRecipe,
            fillingId: editedSubOrder.fillingId,
            count: editedSubOrder.count,
            recipeCounter: editedSubOrder.recipeCounter,
            additionalRecipeCost: user?.additionalRecipeCost,
            packages,
            selectedPackages: editedSubOrder.packages,
            decorations,
            selectedDecorations: editedSubOrder.decorations,
        });

        setEditedSubOrder((prev) => prev.productCost === tempCost ? prev : {...prev, productCost: tempCost});
        setRecipeCost(tempCost);
        setNewProfit(roundMoney(editedSubOrder.price - tempCost));
    };

    const setFile = (f: File[]) => {
        const filteredFiles = f.filter((file) => {
            return !fileMap.has(file.name);
        });

        const newMap = new Map(fileMap);

        filteredFiles.forEach((file) => {
            newMap.set(file.name, file);
        });

        setFileMap(newMap);
    };

    const removeFile = (fileName: string) => {
        setFileMap((prevMap) => {
            const newMap = new Map(prevMap);
            newMap.delete(fileName);
            return newMap;
        });
    };

    const handleRemoveImage = async (previewUrl: string) => {
        const originalFileName = urlMap.get(previewUrl);

        if (!originalFileName) return;

        const fileToRemove = fileMap.get(originalFileName);
        if (!fileToRemove) return;

        const hash = await getFileHash(fileToRemove);

        setPreviewUrls((prevUrls) => (prevUrls ? prevUrls.filter((url) => url !== previewUrl) : null));

        setFileMap((prevMap) => {
            const newMap = new Map(prevMap);
            newMap.delete(originalFileName);
            return newMap;
        });

        setUrlMap((prevMap) => {
            const newMap = new Map(prevMap);
            newMap.delete(previewUrl);
            return newMap;
        });

        setFileHashes((prevHashes) => {
            const newSet = new Set(prevHashes);
            newSet.delete(hash);
            return newSet;
        });
        setEditedSubOrder(prev => ({...prev, images: prev.images?.filter(image => image.fileName !== originalFileName)}));
        URL.revokeObjectURL(previewUrl);
        removeFile(fileToRemove.name);
    };

    const uploadFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files) return;

        const files = Array.from(e.target.files);
        const newFileMap = new Map(fileMap);
        const newUrlMap = new Map(urlMap);
        const newHashes = new Set(fileHashes);
        const updatedPreviewUrls: string[] = [];

        for (const file of files) {
            const hash = await getFileHash(file);
            if (newHashes.has(hash)) {
                continue;
            }

            const renamedFile = rememberOriginalName(new File([file], crypto.randomUUID(), {
                type: file.type,
                lastModified: file.lastModified,
            }), file);

            const previewUrl = URL.createObjectURL(renamedFile);

            newFileMap.set(renamedFile.name, renamedFile); // используем новое имя
            newUrlMap.set(previewUrl, renamedFile.name);
            newHashes.add(hash);
            updatedPreviewUrls.push(previewUrl);
        }

        if (updatedPreviewUrls.length > 0) {
            setPreviewUrls((prev) => [...(prev || []), ...updatedPreviewUrls]);
            setFileMap(newFileMap);
            setUrlMap(newUrlMap);
            setFileHashes(newHashes);
        }

        e.target.value = ''; // очистка input
    };

    async function getFileHash(file: File): Promise<string> {
        const buffer = await file.arrayBuffer();
        const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
        return Array.from(new Uint8Array(hashBuffer))
            .map((b) => b.toString(16).padStart(2, '0'))
            .join('');
    }

    useEffect(() => {
        const recipe = recipes?.find((el) => el.id === subOrder.recipeId);
        if (!recipe) return;

        setSelectedRecipe(recipe);
        setInitRender(false);
    }, [recipes, subOrder.recipeId]);

    const [editEffect, setEditEffect] = useState<boolean>(false);
    useEffect(() => {
        if (!initRender && editedSubOrder.fillingId !== 0 && (!selectedRecipe?.fillings || selectedRecipe.fillings.length == 0)) {
            setFillingName('');
            setEditedSubOrder((prev) => {
                return {
                    ...prev,
                    fillingId: 0,
                    fillingName: '',
                };
            });
        }

        saveSubOrderChanges(editedSubOrder);
        setEditEffect(true);
    }, [editedSubOrder]);

    useEffect(() => {
        if (editEffect) {
            const filteredMap = new Map(Array.from(fileMap.entries()).filter(([key, file]) => existingImages.every((el) => el.fileName !== file.name)));
            handleNewFiles(filteredMap);
            setEditEffect(false);
        }
    }, [editEffect]);

    useEffect(() => {
        // TODO: check files and need to switch state when add photo.
        const files = Array.from(fileMap.values());

        const existingImages = editedSubOrder.images || [];
        const images: IOrderImage[] = [...existingImages];

        files.forEach((el) => {
            const alreadyExists = existingImages.some((x) => x.fileName === el.name);
            if (!alreadyExists) {
                images.push({
                    id: el.name,
                    subOrderId: editedSubOrder.id,
                    orderId: editedSubOrder.orderId,
                    fileName: el.name,
                    isResultImage: false,
                    state: DTO_STATE.DTO_NEW,
                });
            }
        });

        const temp: IOrderImage[] = images.filter((x) => Boolean(x.imageId) || (typeof x.id === 'number' && x.id > 0) || (x.id === x.fileName && files.some((f) => f.name === x.fileName)));
        const state = editedSubOrder.state === DTO_STATE.DTO_NEW || editedSubOrder.state === DTO_STATE.DTO_DELETED ? editedSubOrder.state : DTO_STATE.DTO_MODIFIED;
        setEditedSubOrder((prev) => ({
            ...prev,
            images: temp,
        }));
    }, [fileMap]);

    /*    useEffect(() => {
        if (!initRender && !loadedImages) {
            const updatedPreviewUrls: string[] = [];
            const newHashes = new Set(fileHashes);
            const loadImage = async () => {
                for (const image of editedSubOrder.images || []) {
                    const { type, blob, error } = image.imageId ? await fetchImageV2(image.imageId) : await fetchImage(image.fileName);

                    const lastModified = image.createdAt instanceof Date ? image.createdAt.getTime() : new Date(image.createdAt || '').getTime();
                    const file = new File([blob!], image.fileName, {
                        type: type || blob?.type,
                        lastModified: lastModified,
                    });

                    const hash = await getFileHash(file);
                    newHashes.add(hash);
                    updatedPreviewUrls.push(URL.createObjectURL(file));
                }
                setFileHashes(newHashes);
                setPreviewUrls((prevFiles) => [...(prevFiles || []), ...updatedPreviewUrls]);
                setLoadedImages(true);
            };

            loadImage();
        }
    }, [initRender]);*/

    useEffect(() => {
        if (!initRender && !loadedImages) {
            const loadImage = async () => {
                const newFileMap = new Map<string, File>();
                const newUrlMap = new Map<string, string>();
                const newPreviewUrls: string[] = [];
                const newHashes = new Set<string>();

                for (const image of editedSubOrder.images || []) {
                    const result = image.imageId ? await fetchImageV2(image.imageId) : await fetchImage(image.fileName);
                    if (!result.imageUrl) continue;
                    const response = await fetch(result.imageUrl, {credentials: 'omit'}).catch(() => null);
                    if (!response?.ok) continue;
                    const blob = await response.blob().catch(() => null);
                    if (!blob) continue;
                    const type = blob.type;
                    if (!blob) continue;

                    const lastModified = image.createdAt instanceof Date ? image.createdAt.getTime() : new Date(image.createdAt || '').getTime();

                    const file = new File([blob], image.fileName, {
                        type: type || blob.type,
                        lastModified: lastModified,
                    });

                    const hash = await getFileHash(file);
                    if (newHashes.has(hash)) continue; // пропускаем дубликаты

                    const previewUrl = URL.createObjectURL(file);

                    newFileMap.set(file.name, file);
                    newUrlMap.set(previewUrl, file.name);
                    newPreviewUrls.push(previewUrl);
                    newHashes.add(hash);
                }

                setFileMap(newFileMap);
                setUrlMap(newUrlMap);
                setPreviewUrls(newPreviewUrls);
                setFileHashes(newHashes);
                setLoadedImages(true);
            };

            loadImage();
        }
    }, [initRender]);

    useEffect(() => {
        calculateProductCost();
    }, [selectedRecipe, editedSubOrder.fillingId, editedSubOrder.count, editedSubOrder.recipeCounter, editedSubOrder.price, editedSubOrder.packages, editedSubOrder.decorations, packages, decorations, user?.additionalRecipeCost]);

    useEffect(() => {
        setSelectedPackagesNames(editedSubOrder.packages
            ?.filter(p => p.state !== DTO_STATE.DTO_DELETED && p.name)
            .map(p => p.name) || []);

        if (!selectedRecipe?.id) {
            return;
        }

        setLastFetchedRecipeId(selectedRecipe?.id as string);
    }, [selectedRecipe?.id, editedSubOrder.packages]);

    // Helper to update state for an image
    const updateImageDtoState = (fileName: string, newState: string) => {
        setEditedSubOrder((prev) => ({
            ...prev,
            images: prev.images?.map((img) => (img.fileName === fileName ? {...img, state: newState} : img)),
        }));
    };
    // Separate existing and new photos
    const existingImages = editedSubOrder.images?.filter((x) => Number(x.id) > 0) || [];
    const newPhotoUrls =
        previewUrls?.filter((url) => {
            const fileName = urlMap.get(url);
            // If fileName is not in existingImages, it's a new photo
            return fileName && !existingImages.some((img) => img.fileName === fileName);
        }) || [];

    return (
        <Box sx={{width: '100%'}}>
            <Stack spacing={2} sx={{minWidth: 100, maxWidth: 390}}>
                <Autocomplete
                    disablePortal
                    id="recipes"
                    placeholder={t('recipes:select_recipe')}
                    options={recipesNameList}
                    value={editedSubOrder.recipeName}
                    renderInput={(params) => <TextField {...params} required
                                                        label={t('recipes:select_recipe')}/>}
                    onChange={handleRecipeName}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                                borderWidth: '2px',
                                borderColor: isValueSelected ? 'green' : 'rgb(211, 47, 47)',
                            },
                            '&:hover fieldset': {
                                borderWidth: '2px',
                                borderColor: isValueSelected ? 'green' : 'rgb(211, 47, 47)',
                            },
                            '&.Mui-focused fieldset': {
                                borderWidth: '2px',
                                borderColor: isValueSelected ? 'green' : 'rgb(211, 47, 47)',
                            },
                        },
                    }}
                />
                <FormControl
                    variant="outlined"
                    size="medium"
                    required
                    error={size === ''}
                    sx={{
                        '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                            borderWidth: '2px',
                            borderColor: size === '0.00' || !size ? 'red' : 'green',
                        },
                    }}
                >
                    <InputLabel id="size_label">{t('orders:service_size')}</InputLabel>
                    <Select
                        labelId="size_label"
                        id="recipe_size"
                        value={size}
                        onChange={handleRecipeCounter}
                        label={t('orders:service_size')}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                    borderWidth: '2px',
                                    borderColor: isValueSelected ? 'green' : 'rgb(211, 47, 47)',
                                },
                                '&:hover fieldset': {
                                    borderWidth: '2px',
                                    borderColor: isValueSelected ? 'green' : 'rgb(211, 47, 47)',
                                },
                                '&.Mui-focused fieldset': {
                                    borderWidth: '2px',
                                    borderColor: isValueSelected ? 'green' : 'rgb(211, 47, 47)',
                                },
                            },
                        }}
                    >
                        {RECIPE_SIZE?.map((option) => (
                            <MenuItem key={option} value={option}>
                                {option}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {selectedRecipe?.fillings && selectedRecipe.fillings.length > 0 && (
                    <FormControl
                        variant="outlined"
                        size="medium"
                        required
                        error={fillingError}
                        sx={{
                            '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                                borderWidth: '2px',
                                borderColor: fillingError ? 'red' : 'green',
                            },
                        }}
                    >
                        <InputLabel id="filling_label">{t('recipes:filling')}</InputLabel>
                        <Select
                            labelId="filling_label"
                            id="filling_select"
                            value={fillingName}
                            onChange={handleFilling}
                            label={t('recipes:filling')}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderWidth: '2px',
                                    },
                                    '&:hover fieldset': {
                                        borderWidth: '2px',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderWidth: '2px',
                                    },
                                },
                            }}
                        >
                            {selectedRecipe.fillings.map((option) => (
                                <MenuItem key={option.fillingName} value={option.fillingName}>
                                    {option.fillingName}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
                {packages && lastFetchedRecipeId === selectedRecipe?.id && (
                    <Box sx={{mt: 2}}>
                        {editedSubOrder.packages?.map((pkg, index) => (
                            <PackageSelector
                                key={pkg.id}
                                options={packages}
                                value={{
                                    packageId: pkg.packageId,
                                    name: pkg.name,
                                    quantity: pkg.quantity,
                                    state: pkg.state
                                }}
                                label={t('settings:packages')}
                                excludedNames={selectedPackagesNames}
                                onChange={(updatedValue) => handleUpdatePackageField(index, updatedValue)}
                                onRemove={() => handleRemovePackageField(index)}
                                onRestore={() => handleRestorePackageField(index)}
                                isDeleted={pkg.state === DTO_STATE.DTO_DELETED}
                                isEditMode={pkg.state !== DTO_STATE.DTO_NEW}
                            />
                        ))}

                        <WriteButton
                            variant="outlined"
                            startIcon={<AddIcon/>} // Импортируйте AddIcon из @mui/icons-material
                            onClick={handleAddPackageField}
                            disabled={isAddDisabled} // Блокируем кнопку здесь
                            sx={{
                                mt: 1,
                                width: '100%',
                                // Опционально: можно добавить визуальный стиль для disabled состояния
                                '&.Mui-disabled': {
                                    opacity: 0.5
                                }
                            }}
                        >
                            {t('orders:add_package')}
                        </WriteButton>
                    </Box>)}
                {!!decorations?.length && lastFetchedRecipeId === selectedRecipe?.id && (
                    <Box sx={{mt: 2}}>
                        <Stack spacing={2}>
                            {editedSubOrder.decorations?.map((decoration) => {
                                const options = decorations
                                    .filter(option => option.id === decoration.decorationId || !selectedDecorationIds.includes(option.id))
                                    .map(option => option.name);

                                return (
                                    <DecorationSelector
                                        key={decoration.id}
                                        options={options}
                                        id={decoration.id}
                                        name={decoration.name}
                                        quantity={decoration.quantity}
                                        onChangeName={handleUpdateDecorationName}
                                        onChangeCount={handleUpdateDecorationCount}
                                        onRemove={handleRemoveDecoration}
                                        onRestore={handleRestoreDecoration}
                                        isDeleted={decoration.state === DTO_STATE.DTO_DELETED}
                                    />
                                );
                            })}
                        </Stack>
                        <WriteButton
                            variant="outlined"
                            startIcon={<AddIcon/>}
                            onClick={handleAddDecorationField}
                            disabled={isAddDecorationDisabled}
                            sx={{mt: 1, width: '100%', '&.Mui-disabled': {opacity: 0.5}}}
                        >
                            {t('orders:add_decoration')}
                        </WriteButton>
                    </Box>
                )}
                <TextField
                    inputRef={inputCountRef}
                    required
                    id={'count'}
                    label={t('items:count')}
                    placeholder={t('items:count')}
                    value={orderCount}
                    focused
                    type="number"
                    onChange={handleOrderCount}
                    onFocus={() => inputCountRef.current && inputCountRef.current.select()}
                    style={{marginBottom: 10}}
                    color={orderCount === 0 ? 'error' : 'success'}
                />
                <LocalizedDecimalField
                    inputRef={inputPriceRef}
                    required
                    id={'price'}
                    label={t('finance:order_price')}
                    placeholder={t('finance:order_price')}
                    value={newPrice}
                    focused
                    onValueChange={handlePrice}
                    onFocus={() => inputPriceRef.current && inputPriceRef.current.select()}
                    InputProps={{
                        endAdornment: <InputAdornment position="end">{getCurrencySymbol(currency)}</InputAdornment>,
                    }}
                    style={{marginBottom: 10}}
                    color={newPrice === 0 ? 'error' : 'success'}
                />

                {Number(newPrice) < Number(editedSubOrder.productCost) && <Box sx={{p: 1.5, borderRadius: 1, bgcolor: 'warning.light', color: 'warning.dark'}}>
                    {t('orders:price_below_cost', {amount: formatCurrency(Number(editedSubOrder.productCost) - Number(newPrice))})}
                </Box>}

                {selectedRecipe && <Box sx={{p: 1.5, borderRadius: 1, bgcolor: 'action.hover'}}>
                    <Box>{t('orders:recommended_price')}: {formatCurrency(recommendation.linePrice ?? 0)}</Box>
                    {Number(newPrice) !== recommendation.linePrice && <WriteButton size="small" onClick={() => {
                        const price = recommendation.linePrice ?? 0;
                        setNewPrice(price);
                        setEditedSubOrder(previous => ({...previous, price}));
                    }}>{t('orders:use_recommended_price')}</WriteButton>}
                    {!recommendation.isComplete && <Box sx={{color: 'warning.main', mt: 0.5}}>{t('orders:recommended_price_missing_for')}: {recommendation.missing.join(', ')}</Box>}
                </Box>}

                <WriteFileInput style={{display: 'none'}} id={`file-upload_${subOrder.id}`} type="file" multiple
                       accept=".jpg,.png,.jpeg" onChange={uploadFile}/>
                <label htmlFor={`file-upload_${subOrder.id}`}>
                    <WriteButton variant="contained" component="span">
                        {t('orders:upload_order_images')}
                    </WriteButton>
                </label>

                {/* Existing Photos Section */}
                {existingImages.length > 0 && (
                    <Box sx={{mt: 2}}>
                        <div style={{fontWeight: 'bold', marginBottom: 8}}>{t('orders:existing_photos')}</div>
                        <Grid container spacing={2}>
                            {existingImages.map((img, idx) => {
                                // Find preview url for this image
                                const previewUrl = Array.from(urlMap.entries()).find(([, name]) => name === img.fileName)?.[0];
                                const isDeleted = img.state === DTO_STATE.DTO_DELETED;
                                return (
                                    <Grid item xs={12} sm={6} md={4} key={img.fileName}>
                                        <Card
                                            sx={{
                                                maxWidth: 345,
                                                position: 'relative',
                                                border: isDeleted ? '2px solid red' : undefined,
                                                background: isDeleted ? '#ffeaea' : undefined,
                                            }}
                                        >
                                            <WriteIconButton
                                                sx={{
                                                    position: 'absolute',
                                                    top: 8,
                                                    right: 8,
                                                    zIndex: 1,
                                                    backgroundColor: 'rgba(255,255,255,0.7)'
                                                }}
                                                onClick={() =>
                                                    isDeleted
                                                        ? updateImageDtoState(img.fileName, DTO_STATE.DTO_UNCHANGED)
                                                        : updateImageDtoState(img.fileName, DTO_STATE.DTO_DELETED)
                                                }
                                            >
                                                {isDeleted ? <RestoreIcon/> : <CloseIcon/>}
                                            </WriteIconButton>
                                            {previewUrl && <CardMedia component="img" height="200" image={previewUrl}
                                                                       alt={t('orders:existing_photo_alt', {number: idx + 1})}/>}
                                        </Card>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </Box>
                )}

                {/* New Photos Section */}
                {newPhotoUrls.length > 0 && (
                    <Box sx={{mt: 2}}>
                        <div style={{fontWeight: 'bold', marginBottom: 8}}>{t('orders:new_photos')}</div>
                        <Grid container spacing={2}>
                            {newPhotoUrls.map((url, index) => (
                                <Grid item xs={12} sm={6} md={4} key={url}>
                                    <Card sx={{maxWidth: 345, position: 'relative'}}>
                                        <WriteIconButton
                                            sx={{
                                                position: 'absolute',
                                                top: 8,
                                                right: 8,
                                                zIndex: 1,
                                                backgroundColor: 'rgba(255,255,255,0.7)'
                                            }}
                                            onClick={() => handleRemoveImage(url)}
                                        >
                                            <CloseIcon/>
                                        </WriteIconButton>
                                        <CardMedia component="img" height="200" image={url} alt={`New ${index + 1}`}/>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                )}

                <Box>
                    {t('finance:order_cost')} {formatCurrency(Number(recipeCost))}
                </Box>
                <Box>
                    {t('finance:order_profit')} {formatCurrency(Number(newProfit))}
                </Box>
                <WriteButton variant="outlined" onClick={deleteSubOrder} sx={buttonStyle}>
                    {t('common:delete')}
                </WriteButton>
            </Stack>
        </Box>
    );
};

export default EditSubOrder;
